import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, takeUntil, combineLatest, debounceTime, distinctUntilChanged, take, Observable } from 'rxjs';

import { LostSaleService } from '../../../lost-sale/lost-sale.service';
import { LostSaleV2StateService } from '../../services/lost-sale-v2-state.service';
import { LostSaleSummaryItem, LostSaleQueryParams } from '../../models/lost-sale.interfaces';
import { CompanyService } from 'app/modules/components/configuration/company/company.service.service';
import { DocumentNavigationComponent } from 'app/common/components/document-navigation/document-navigation.component';
import { DocumentTypeEnum } from 'app/common/enums/document-types.enum';
import { UnitMeasureDisplayComponent } from 'app/shared/components/unit-measure-display/unit-measure-display.component';

@Component({
  selector: 'app-lost-sale-summary',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    DocumentNavigationComponent,
    UnitMeasureDisplayComponent
  ],
  templateUrl: './lost-sale-summary.component.html',
  styleUrls: ['./lost-sale-summary.component.scss']
})
export class LostSaleSummaryComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  private destroy$ = new Subject<void>();
  private hasInitialLoad = false;

  displayedColumns: string[] = [
    'material',
    'materialGroup',
    'customer',
    'numberOrder',
    'invoiceDocument',
    'sellerToName',
    'locationTwo',
    'ordersCount',
    'totalSoldOriginalQuantity',
    'totalSoldQuantity',
    'totalConfirmedQuantity',
    'totalDeliveredQuantity',
    'totalInvoicedQuantity',
    'sumSoldQuantity',
    'sumStockConfirmedERP',
    'sumDeliveredQuantity',
    'sumInvoiceQuantity',
    'lostSaleAmount',
    'commercialLostSale',
    'logisticLostSale',
    'realLostSale',
    'commercialLostSalePercentage',
    'logisticLostSalePercentage',
    'realLostSalePercentage'
  ];

  dataSource = new MatTableDataSource<LostSaleSummaryItem>([]);
  totalItems = 0;
  UMPActive: boolean = false;
  pageSize = 100;
  pageIndex = 0;
  pageSizeOptions = [25, 50, 100, 200, 500];

  totals = {
    totalCommercialLostSaleUMB: 0,
    totalCommercialLostSaleUMP: 0,
    totalLogisticLostSaleUMB: 0,
    totalLogisticLostSaleUMP: 0,
    totalRealLostSaleUMB: 0,
    totalRealLostSaleUMP: 0
  };

  DocumentTypeEnum = DocumentTypeEnum;

  constructor(
    private stateService: LostSaleV2StateService,
    private lostSaleService: LostSaleService,
    private snackBar: MatSnackBar,
    private companyService: CompanyService
  ) {
    this.isLoading$ = this.stateService.loading$;
  }

  ngOnInit(): void {
    this.setupTableConfiguration();
    this.subscribeToStateChanges();
    this.getCompanyConfiguration();
  }

  isLoading$: Observable<boolean>;

  private setupTableConfiguration(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  private subscribeToStateChanges(): void {
    this.stateService.queryParamsChanged$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.loadData();
      this.hasInitialLoad = true;
    });

    this.stateService.summaryPage$.pipe(
      takeUntil(this.destroy$),
      distinctUntilChanged((prev, curr) =>
        prev.pageIndex === curr.pageIndex && prev.pageSize === curr.pageSize
      )
    ).subscribe((pagination) => {
      this.pageIndex = pagination.pageIndex;
      this.pageSize = pagination.pageSize;
    });

    this.stateService.summarySort$.pipe(
      takeUntil(this.destroy$),
      distinctUntilChanged((prev, curr) =>
        prev.active === curr.active && prev.direction === curr.direction
      )
    ).subscribe(() => {
      if (this.hasInitialLoad) {
        this.stateService.loading$.pipe(take(1)).subscribe(loading => {
          if (!loading) {
            this.loadData();
          }
        });
      }
    });
  }

  private loadData(): void {
    if (this.stateService.loadingState) {
      return;
    }

    this.stateService.setLoading(true);

    const params: LostSaleQueryParams = {
      filters: this.stateService.getCurrentFilters(),
      search: this.stateService.getCurrentSearchTerm(),
      page: this.pageIndex + 1,
      pageSize: this.pageSize,
      orderBy: this.stateService.getCurrentSummarySort().active,
      orderDirection: this.stateService.getCurrentSummarySort().direction === 'desc' ? 'DESC' : 'ASC'
    };

    this.lostSaleService.findLostSaleSummaryV2(params).subscribe({
      next: (response) => {
        const actualData = response.data[0].data;
        const actualTotalCount = response.data[0].pageInfo?.[0]?.total || response.data[0].pageInfo?.total || 0;
        const actualTotals = response.totals;
        const items = this.transformSummaryData(actualData);

        this.dataSource.data = items;
        this.totalItems = actualTotalCount;

        if (actualTotals) {
          this.totals = actualTotals;
        } else {
          this.calculateTotals(items);
        }

        this.stateService.setSummaryData(items, actualTotalCount);
        this.stateService.setLoading(false);
      },
      error: (error) => {
        this.handleError(error);
        this.stateService.setLoading(false);
      }
    });
  }

  private transformSummaryData(data: any[]): LostSaleSummaryItem[] {
    return data.map(item => {
      const materialName = item.materialName?.name || '';
      const materialCode = item.materialName?.code || item.materialName?.externalKeys?.code || '';
      const materialGroupName = item.materialName?.materialGroups?.[0]?.name || '';

      const customerName = item.shipToName || '';
      const customerCode = item.shipToExternalKeyCode || '';

      const totalSoldQuantityUMB = item.sumSoldQuantity || 0;
      const totalDeliveredQuantityUMB = item.sumDeliveredQuantity || 0;
      const totalInvoicedQuantityUMB = item.sumInvoiceQuantity || 0;

      const conversionFactor = item.pararellConversionFactor || 0;
      const totalSoldQuantityUMP = totalSoldQuantityUMB * conversionFactor;
      const totalDeliveredQuantityUMP = totalDeliveredQuantityUMB * conversionFactor;
      const totalInvoicedQuantityUMP = totalInvoicedQuantityUMB * conversionFactor;

      const totalSoldOriginalQuantityUMB = totalSoldQuantityUMB;
      const totalSoldOriginalQuantityUMP = totalSoldQuantityUMP;

      const totalConfirmedQuantityUMB = item.sumStockConfirmedERP || totalSoldQuantityUMB;
      const totalConfirmedQuantityUMP = totalConfirmedQuantityUMB * conversionFactor;

      const commercialLostSaleUMB = item.lostSale || 0;
      const commercialLostSaleUMP = commercialLostSaleUMB * conversionFactor;

      const logisticLostSaleUMB = totalSoldQuantityUMB - totalDeliveredQuantityUMB;
      const logisticLostSaleUMP = totalSoldQuantityUMP - totalDeliveredQuantityUMP;

      const realLostSaleUMB = totalSoldQuantityUMB - totalInvoicedQuantityUMB;
      const realLostSaleUMP = totalSoldQuantityUMP - totalInvoicedQuantityUMP;

      const ordersCount = typeof item.serials === 'string' ? 1 :
                         (Array.isArray(item.serials) ? item.serials.length : 1);

      return {
        materialId: item.materialName?._id || '',
        materialName: materialName,
        materialCode: materialCode,
        materialGroupId: item.materialName?.materialGroups?.[0]?._id || '',
        materialGroupName: materialGroupName,
        customerId: parseInt(customerCode || '0', 10),
        customerName: customerName,
        customerCode: customerCode,

        totalSoldOriginalQuantityUMB,
        totalSoldOriginalQuantityUMP,
        totalSoldQuantityUMB,
        totalSoldQuantityUMP,
        totalConfirmedQuantityUMB,
        totalConfirmedQuantityUMP,
        totalDeliveredQuantityUMB,
        totalDeliveredQuantityUMP,
        totalInvoicedQuantityUMB,
        totalInvoicedQuantityUMP,

        commercialLostSaleUMB,
        commercialLostSaleUMP,
        logisticLostSaleUMB,
        logisticLostSaleUMP,
        realLostSaleUMB,
        realLostSaleUMP,

        commercialLostSalePercentage: this.calculatePercentage(totalSoldQuantityUMB, commercialLostSaleUMB),
        logisticLostSalePercentage: this.calculatePercentage(totalSoldQuantityUMB, logisticLostSaleUMB),
        realLostSalePercentage: this.calculatePercentage(totalSoldQuantityUMB, realLostSaleUMB),

        ordersCount: ordersCount,

        // Missing fields from original report
        numberOrder: item.numberOrder || item.serials || '',
        invoiceDocument: item.invoiceDocument?.externalDocument || '',
        sellerToName: item.sellerToName || '',
        sellerToCode: item.sellerToExternalKeyCode || '',
        locationTwo: item.locationTwo?.name || '',
        // Monetary values in local currency
        sumSoldQuantity: item.sumSoldQuantity || 0,
        sumStockConfirmedERP: item.sumStockConfirmedERP || 0,
        sumDeliveredQuantity: item.sumDeliveredQuantity || 0,
        sumInvoiceQuantity: item.sumInvoiceQuantity || 0,
        lostSaleAmount: item.lostSale || 0,
        currency: item.currency,

        // Units of measure mapping
        unitOfMeasure: {
          base: {
            id: item.baseUnitOfMeasure?._id || '',
            name: item.baseUnitOfMeasure?.name || '',
            code: item.baseUnitOfMeasure?.code || ''
          },
          parallel: {
            id: item.pararellUnitOfMeasure?._id || '',
            name: item.pararellUnitOfMeasure?.name || '',
            code: item.pararellUnitOfMeasure?.code || ''
          },
          sale: {
            id: item.soldUnitOfMeasure?._id || '',
            name: item.soldUnitOfMeasure?.name || '',
            code: item.soldUnitOfMeasure?.code || ''
          }
        }
      } as LostSaleSummaryItem;
    });
  }

  private calculateTotals(items: LostSaleSummaryItem[]): void {
    this.totals = items.reduce((acc, item) => {
      return {
        totalCommercialLostSaleUMB: acc.totalCommercialLostSaleUMB + item.commercialLostSaleUMB,
        totalCommercialLostSaleUMP: acc.totalCommercialLostSaleUMP + item.commercialLostSaleUMP,
        totalLogisticLostSaleUMB: acc.totalLogisticLostSaleUMB + item.logisticLostSaleUMB,
        totalLogisticLostSaleUMP: acc.totalLogisticLostSaleUMP + item.logisticLostSaleUMP,
        totalRealLostSaleUMB: acc.totalRealLostSaleUMB + item.realLostSaleUMB,
        totalRealLostSaleUMP: acc.totalRealLostSaleUMP + item.realLostSaleUMP
      };
    }, {
      totalCommercialLostSaleUMB: 0,
      totalCommercialLostSaleUMP: 0,
      totalLogisticLostSaleUMB: 0,
      totalLogisticLostSaleUMP: 0,
      totalRealLostSaleUMB: 0,
      totalRealLostSaleUMP: 0
    });
  }

  private calculatePercentage(original: number, lostAmount: number): number {
    if (!original || original === 0) return 0;
    return ((lostAmount || 0) / original) * 100;
  }

  private getCompanyConfiguration(): void {
    this.companyService.findToComboBox().subscribe({
      next: (res) => {
        if (res.data && res.data.length > 0) {
          this.UMPActive = res.data[0].UMPActive || false;
        }
      },
      error: (error) => {
        this.handleError(error);
      }
    });
  }

  private handleError(error: any): void {
    const message = error.error?.message || 'Error al cargar resumen de venta perdida';
    this.snackBar.open(message, 'Cerrar', {
      duration: 5000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: ['error-snackbar']
    });
  }

  // Table event handlers
  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.stateService.setSummaryPage(event.pageIndex, event.pageSize);
  }

  onSortChange(sort: Sort): void {
    this.stateService.setSummarySort(sort.active, sort.direction as 'asc' | 'desc' | '');
  }

  // Helper methods for template
  formatNumber(value: number | undefined | null): string {
    if (value === null || value === undefined) return '0';
    return value.toLocaleString('es-HN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  formatPercentage(value: number | undefined | null): string {
    if (value === null || value === undefined) return '0%';
    return value.toFixed(2) + '%';
  }

  formatCurrency(value: number | undefined | null): string {
    if (value === null || value === undefined) return `0.00`;
    return value.toLocaleString('es-HN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  getLostSaleClass(value: number): string {
    if (value > 0) {
      return 'text-red-600 font-semibold';
    }
    return '';
  }

  getPercentageClass(value: number): string {
    if (value > 10) return 'text-red-600 font-semibold';
    if (value > 5) return 'text-yellow-600';
    return '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}